/**
 * ListMobileCard Component
 *
 * A reusable mobile card component for list items with consistent styling.
 * Provides a standardized layout for mobile list views with:
 * - Primary/secondary text
 * - Optional avatar or icon
 * - Badges/tags
 * - Action buttons
 *
 * @example
 * ```tsx
 * <ListMobileCard
 *   avatar={<UserAvatar name="John Doe" />}
 *   primaryText="John Doe"
 *   secondaryText="john.doe@example.com"
 *   tertiaryText="Last active: 2 hours ago"
 *   badges={[
 *     { label: 'Active', variant: 'success' },
 *     { label: 'Admin', variant: 'primary' },
 *   ]}
 *   actions={[
 *     { icon: <Edit size={16} />, label: 'Edit', onClick: handleEdit },
 *   ]}
 * />
 * ```
 */
import React from 'react';

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';

export interface MobileCardBadge {
  /** Badge label text */
  label: string;
  /** Badge color variant */
  variant?: BadgeVariant;
  /** Custom CSS class (overrides variant) */
  className?: string;
}

export interface MobileCardAction {
  /** Icon to display */
  icon: React.ReactNode;
  /** Action label (shown as tooltip/aria-label) */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Optional CSS class */
  className?: string;
  /** Whether the action is disabled */
  disabled?: boolean;
}

export interface ListMobileCardProps {
  /** Optional avatar or icon on the left */
  avatar?: React.ReactNode;
  /** Primary text (usually name or title) */
  primaryText: React.ReactNode;
  /** Secondary text (usually description or email) */
  secondaryText?: React.ReactNode;
  /** Tertiary text (usually date or additional info) */
  tertiaryText?: React.ReactNode;
  /** Array of badges to display */
  badges?: MobileCardBadge[];
  /** Array of action buttons */
  actions?: MobileCardAction[];
  /** Optional content to render at the top-right */
  topRightContent?: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Click handler for the card (excluding action buttons) */
  onClick?: () => void;
  /** Optional CSS class for the container */
  className?: string;
}

const getBadgeClasses = (variant: BadgeVariant = 'default'): string => {
  const variants: Record<BadgeVariant, string> = {
    default: 'bg-gray-100 text-gray-700 border border-gray-200',
    primary: 'bg-blue-50 text-blue-700 border border-blue-200',
    success: 'bg-green-50 text-green-700 border border-green-200',
    warning: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
    error: 'bg-red-50 text-red-700 border border-red-200',
    info: 'bg-purple-50 text-purple-700 border border-purple-200',
  };
  return variants[variant];
};

const ListMobileCard: React.FC<ListMobileCardProps> = ({
  avatar,
  primaryText,
  secondaryText,
  tertiaryText,
  badges = [],
  actions = [],
  topRightContent,
  footer,
  onClick,
  className = '',
}) => {
  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger card click if clicking on an action button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onClick?.();
  };

  return (
    <div
      className={`p-4 ${onClick ? 'cursor-pointer active:bg-gray-50' : ''} ${className}`}
      onClick={onClick ? handleClick : undefined}
    >
      <div className="flex items-start gap-3">
        {/* Avatar/Icon */}
        {avatar && <div className="flex-shrink-0">{avatar}</div>}

        {/* Main Content - Left Aligned */}
        <div className="flex-1 min-w-0 text-left">
          {/* Top Row: Primary Text + Top Right Content */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0 text-left">
              {/* Primary Text */}
              <div className="text-sm font-semibold text-gray-900 truncate text-left">{primaryText}</div>

              {/* Secondary Text */}
              {secondaryText && (
                <div className="text-sm text-gray-600 truncate mt-0.5 text-left">{secondaryText}</div>
              )}

              {/* Tertiary Text */}
              {tertiaryText && (
                <div className="text-xs text-gray-500 mt-1 text-left">{tertiaryText}</div>
              )}
            </div>

            {/* Top Right Content */}
            {topRightContent && <div className="flex-shrink-0">{topRightContent}</div>}
          </div>

          {/* Badges Row - Left Aligned */}
          {badges.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2 justify-start">
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    badge.className || getBadgeClasses(badge.variant)
                  }`}
                >
                  {badge.label}
                </span>
              ))}
            </div>
          )}

          {/* Footer */}
          {footer && <div className="mt-3 pt-3 border-t border-gray-100 text-left">{footer}</div>}
        </div>

        {/* Action Buttons */}
        {actions.length > 0 && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                }}
                disabled={action.disabled}
                className={
                  action.className ||
                  'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                }
                title={action.label}
                aria-label={action.label}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListMobileCard;
